import React from 'react';
import { useTranslation } from 'react-i18next';
import { Award, Clock, Sofa, Shield, Sparkle } from 'lucide-react';

/**
 * The GO VIP Experience — 5 pillars section.
 * Editorial 2-column layout: bold statement left, pillars grid right.
 * Dark background for premium contrast; used between Services and Vehicle.
 */

const PILLARS = [
  { key: 'professionalism', icon: Award },
  { key: 'punctuality',     icon: Clock },
  { key: 'comfort',         icon: Sofa },
  { key: 'discretion',      icon: Shield },
  { key: 'attention',       icon: Sparkle },
];

export const GoVipExperience = () => {
  const { t } = useTranslation();

  return (
    <section
      className="relative overflow-hidden bg-[#0a0a0a] py-24 md:py-32"
      data-testid="govip-experience-section"
    >
      {/* Ambient gold glow (bottom-right) */}
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-40 -right-20 h-[500px] w-[500px] rounded-full opacity-25 blur-3xl"
        style={{ background: 'radial-gradient(circle, rgba(245,158,11,0.55), transparent 70%)' }}
      />
      {/* Subtle grain */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.05] mix-blend-overlay"
        style={{
          backgroundImage:
            'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\'/%3E%3C/svg%3E")',
        }}
      />

      <div className="container relative mx-auto max-w-6xl px-6 md:px-8">
        <div className="grid gap-12 lg:grid-cols-5 lg:gap-16">
          {/* Left: Bold statement (2 cols) */}
          <div className="lg:col-span-2">
            <div className="mb-5 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.3em] text-amber-400">
              <span className="h-px w-10 bg-amber-500" />
              {t('experience.kicker')}
            </div>
            <h2 className="font-serif text-4xl font-bold leading-[1.05] tracking-tight text-white md:text-5xl lg:text-[3.25rem]">
              {t('experience.title')}
            </h2>
            <p className="mt-6 text-base leading-relaxed text-zinc-400 md:text-lg">
              {t('experience.subtitle')}
            </p>
          </div>

          {/* Right: 5 pillars (3 cols) */}
          <div className="lg:col-span-3">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {PILLARS.map((p, i) => {
                const Icon = p.icon;
                return (
                  <div
                    key={p.key}
                    className={`group rounded-2xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-sm transition-all duration-500 hover:border-amber-400/40 hover:bg-white/[0.06] ${
                      // Make the 5th pillar span 2 cols on tablet+ for pleasing asymmetry
                      i === 4 ? 'sm:col-span-2' : ''
                    }`}
                    data-testid={`pillar-${p.key}`}
                  >
                    <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-lg bg-amber-500/10 text-amber-300 ring-1 ring-amber-400/30 transition-all group-hover:bg-amber-500/20">
                      <Icon className="h-5 w-5" strokeWidth={1.6} />
                    </div>
                    <h3 className="mb-1.5 font-serif text-xl font-semibold text-white">
                      {t(`experience.pillars.${p.key}.title`)}
                    </h3>
                    <p className="text-sm leading-relaxed text-zinc-400">
                      {t(`experience.pillars.${p.key}.desc`)}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GoVipExperience;
